import { useEffect } from 'react'
import { EditorContent, useEditor, useEditorState } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'

import { getClassName, LinkIcon, UnlinkIcon, BulletListIcon, normalizeRichTextForStorage } from './auctionsadminutil'

import css from './sass/texteditor.module.scss'
import editCss from './sass/createedit.module.scss'

const normalizeStoredValue = (currentValue = '') => normalizeRichTextForStorage(currentValue)

const normalizeEditorValue = (currentEditor) => {
  const plainText = currentEditor.getText().trim()
  if (!plainText) return ''
  return normalizeStoredValue(currentEditor.getHTML())
}

const TextEditor = ({ value = '', onChange, isReadOnly = false, needsAsterisk, editorId }) => {
  const getButtonClassName = ({ isActive = false, isDisabled = false }) =>
    [ css['toolbar-button'], isActive ? css['toolbar-button-active'] : '', isDisabled ? css['toolbar-button-disabled'] : '', ]
      .filter(Boolean).join(' ')

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        link: {
          openOnClick: false,
          enableClickSelection: true,
          autolink: true,
          linkOnPaste: true,
          HTMLAttributes: {
            target: '_blank',
            rel: 'noopener noreferrer nofollow',
          },
          validate: href => /^https?:\/\//i.test(href),
        },
      }),
    ],
    content: value,
    editable: !isReadOnly,
    shouldRerenderOnTransaction: false,
    editorProps: {
      attributes: editorId ? { id: editorId } : {},
      handleClick: (view, pos, event) => {
        const target = event.target

        if (target instanceof HTMLElement && target.closest('a')) {
          event.preventDefault()
        }

        return false
      },
    },
    onUpdate: ({ editor: nextEditor }) => {
      onChange?.(normalizeEditorValue(nextEditor))
    },
  })

  const editorState = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => {
      if (!currentEditor) {
        return {
          isBold: false,
          isItalic: false,
          isUnderline: false,
          isLink: false,
          isBulletList: false,
          canBold: false,
          canItalic: false,
          canUnderline: false,
          canBulletList: false,
        }
      }

      return {
        isBold: currentEditor.isActive('bold'),
        isItalic: currentEditor.isActive('italic'),
        isUnderline: currentEditor.isActive('underline'),
        isLink: currentEditor.isActive('link'),
        isBulletList: currentEditor.isActive('bulletList'),
        canBold: currentEditor.can().chain().toggleBold().run(),
        canItalic: currentEditor.can().chain().toggleItalic().run(),
        canUnderline: currentEditor.can().chain().toggleUnderline().run(),
        canBulletList: currentEditor.can().chain().toggleBulletList().run(),
      }
    },
  })

  useEffect(() => {
    if (!editor) return

    const nextValue = value || ''
    const normalizedNextValue = normalizeStoredValue(nextValue)
    const normalizedEditorValue = normalizeEditorValue(editor)

    // Avoid replacing the document with equivalent normalized content, which resets the caret.
    if (normalizedNextValue !== normalizedEditorValue) {
      editor.commands.setContent(nextValue, { emitUpdate: false })
    }
  }, [editor, value])

  useEffect(() => {
    if (!editor) return

    editor.setEditable(!isReadOnly)
  }, [editor, isReadOnly])

  if (!editor) return null

  const keepSelection = event => {
    if (isReadOnly) return
    event.preventDefault()
  }

  const focusEditorFromContainer = event => {
    if (isReadOnly) return

    const target = event.target

    if (target === event.currentTarget) {
      event.preventDefault()
      editor.chain().focus('end').run()
    }
  }

  const setLink = () => {
    if (isReadOnly) return

    const currentHref = editor.getAttributes('link').href || ''
    const input = prompt('Enter URL', currentHref)

    if (input === null) return

    const trimmedInput = input.trim()

    if (!trimmedInput) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }

    const href = /^https?:\/\//i.test(trimmedInput)
      ? trimmedInput
      : `https://${trimmedInput}`

    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href })
      .run()
  }

  return (
    <div className={css['editor-shell']}>
      <div>
        <label>Description{needsAsterisk && <span className={editCss['asterisk']}>*</span>}</label>
        <div className={css.toolbar} role="toolbar" aria-label="Text formatting">
          <button
            type="button"
            className={getButtonClassName({
              isActive: editorState.isBold,
              isDisabled: isReadOnly || !editorState.canBold,
            })}
            aria-label="Bold"
            aria-pressed={editorState.isBold}
            title="Bold"
            disabled={isReadOnly || !editorState.canBold}
            onMouseDown={keepSelection}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <strong aria-hidden="true">B</strong>
          </button>

          <button
            type="button"
            className={getButtonClassName({
              isActive: editorState.isItalic,
              isDisabled: isReadOnly || !editorState.canItalic,
            })}
            aria-label="Italic"
            aria-pressed={editorState.isItalic}
            title="Italic"
            disabled={isReadOnly || !editorState.canItalic}
            onMouseDown={keepSelection}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <em aria-hidden="true">I</em>
          </button>

          <button
            type="button"
            className={getButtonClassName({
              isActive: editorState.isUnderline,
              isDisabled: isReadOnly || !editorState.canUnderline,
            })}
            aria-label="Underline"
            aria-pressed={editorState.isUnderline}
            title="Underline"
            disabled={isReadOnly || !editorState.canUnderline}
            onMouseDown={keepSelection}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <u aria-hidden="true">U</u>
          </button>

          <span className={css['toolbar-separator']} aria-hidden="true" />

          <button
            type="button"
            className={getButtonClassName({
              isActive: editorState.isLink,
              isDisabled: isReadOnly,
            })}
            aria-label="Link"
            aria-pressed={editorState.isLink}
            title="Link"
            disabled={isReadOnly}
            onMouseDown={keepSelection}
            onClick={setLink}
          >
            <LinkIcon />
          </button>

          <button
            type="button"
            className={getButtonClassName({
              isDisabled: isReadOnly || !editorState.isLink,
            })}
            aria-label="Unlink"
            disabled={isReadOnly || !editorState.isLink}
            title="Unlink"
            onMouseDown={keepSelection}
            onClick={() => editor.chain().focus().extendMarkRange('link').unsetLink().run()}
          >
            <UnlinkIcon />
          </button>

          <span className={css['toolbar-separator']} aria-hidden="true" />

          <button
            type="button"
            className={getButtonClassName({
              isActive: editorState.isBulletList,
              isDisabled: isReadOnly || !editorState.canBulletList,
            })}
            aria-label="Bullet list"
            aria-pressed={editorState.isBulletList}
            disabled={isReadOnly || !editorState.canBulletList}
            title="Bullet list"
            onMouseDown={keepSelection}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <BulletListIcon />
          </button>
        </div>
      </div>

      <EditorContent
        editor={editor}
        className={getClassName(css['editor-content'], isReadOnly && css['editor-content-readonly'])}
        onMouseDown={focusEditorFromContainer}
      />
    </div>
  )
}

export default TextEditor
